import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { Calendar, TrendingUp, Filter, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HistoricalDataFilter = ({ onDataLoaded }) => {
  const { token } = useAuth();
  const [dateRange, setDateRange] = useState('30');
  const [metricType, setMetricType] = useState('all');
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date(Date.now() - parseInt(dateRange) * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      
      const response = await fetch(
        `${API_URL}/api/dashboard/historical?start_date=${startDate}&end_date=${endDate}&metric_type=${metricType}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHistoricalData(data);
        if (onDataLoaded) onDataLoaded(data);
      } else {
        toast.error('Failed to load historical data');
      }
    } catch (error) {
      console.error('Error fetching historical data:', error);
      toast.error('Error loading historical data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoricalData();
  }, [dateRange, metricType, token]);

  // Process data for charts
  const processChartData = () => {
    if (!historicalData?.data_points) return [];
    
    const dateMap = {};
    historicalData.data_points.forEach(point => {
      const date = point.date;
      if (!dateMap[date]) {
        dateMap[date] = { date };
      }
      Object.entries(point.metrics || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          dateMap[date][key] = value;
        }
      });
    });
    
    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  };

  const chartData = processChartData();

  const COLORS = {
    pain_controlled_48h: '#0f172a',
    dyspnea_improvement_48h: '#0d9488',
    adc: '#6366f1',
    falls_total: '#ef4444',
    medication_errors: '#f59e0b',
    // New operations metrics colors
    conversion_ratio: '#8b5cf6',
    weekly_discharges: '#06b6d4',
    median_length_of_stay: '#f97316',
    net_census_change: '#22c55e'
  };

  return (
    <Card className="w-full" data-testid="historical-data-filter">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-teal-600" />
          Historical Data Trends
        </CardTitle>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Period:</Label>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-32" data-testid="date-range-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="14">Last 14 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="60">Last 60 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Metrics:</Label>
            <Select value={metricType} onValueChange={setMetricType}>
              <SelectTrigger className="w-32" data-testid="metric-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Metrics</SelectItem>
                <SelectItem value="clinical">Clinical</SelectItem>
                <SelectItem value="program">Program</SelectItem>
                <SelectItem value="safety">Safety</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchHistoricalData}
            disabled={loading}
            data-testid="refresh-historical-btn"
          >
            <Filter className="h-4 w-4 mr-1" />
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        {chartData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                
                {metricType === 'all' || metricType === 'clinical' ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="pain_controlled_48h" 
                      name="Pain Control %" 
                      stroke={COLORS.pain_controlled_48h}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="dyspnea_improvement_48h" 
                      name="Dyspnea Improvement %" 
                      stroke={COLORS.dyspnea_improvement_48h}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </>
                ) : null}
                
                {metricType === 'all' || metricType === 'program' ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="adc" 
                      name="ADC" 
                      stroke={COLORS.adc}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="conversion_ratio" 
                      name="Conversion Ratio %" 
                      stroke={COLORS.conversion_ratio}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="weekly_discharges" 
                      name="Weekly Discharges" 
                      stroke={COLORS.weekly_discharges}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="net_census_change" 
                      name="Net Census Change" 
                      stroke={COLORS.net_census_change}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </>
                ) : null}
                
                {metricType === 'all' || metricType === 'safety' ? (
                  <>
                    <Line 
                      type="monotone" 
                      dataKey="falls_total" 
                      name="Falls" 
                      stroke={COLORS.falls_total}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="medication_errors" 
                      name="Med Errors" 
                      stroke={COLORS.medication_errors}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </>
                ) : null}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No historical data available for the selected period.</p>
              <p className="text-sm mt-1">Enter data through the Data Entry page to see trends.</p>
            </div>
          </div>
        )}
        
        {historicalData?.date_range && (
          <div className="mt-4 flex justify-between text-sm text-muted-foreground">
            <span>From: {historicalData.date_range.start}</span>
            <span>To: {historicalData.date_range.end}</span>
            <span>{chartData.length} data points</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HistoricalDataFilter;
